export interface BlogResourceLink {
  label: string;
  href: string;
  external?: boolean;
}

export interface BlogSection {
  id: string;
  title: string;
  description: string;
  icon: string;
  accent: string;
  points?: string[];
  cta?: {
    label: string;
    href: string;
    external?: boolean;
  };
}

export interface BlogHighlightGroup {
  title: string;
  icon: string;
  accent: string;
  links: BlogResourceLink[];
}

export const BLOG_HERO = {
  title: 'Welcome to the KudosWall Blog!',
  description:
    'Your journey to career success starts here. At KudosWall, we empower professionals with tools and resources to excel in their job search and career growth.',
};

export const BLOG_INTRO = {
  title: 'Explore Our Key Resources',
  description:
    "At KudosWall, we believe that every professional deserves the tools to succeed in their career journey. Whether you're a recent graduate crafting your first resume or a seasoned professional aiming for a leadership role, our resources are tailored to meet your needs. From detailed resume examples and actionable guides to cutting-edge AI tools, we're here to help you stand out in a competitive job market. Explore what we offer and take the first step toward your dream job.",
};

export const BLOG_SECTIONS: BlogSection[] = [
  {
    id: 'resume-examples',
    title: 'Resume Examples for Every Profession',
    description:
      'Finding the right way to showcase your skills and experience can be a challenge. That’s why we’ve curated a comprehensive collection of resume examples for every profession.',
    icon: 'FileText',
    accent: 'teal',
    points: [
      'Industries Covered: Explore resumes tailored for engineering, IT, healthcare, education, sales, marketing, and more.',
      'Career Levels: Whether you’re a fresher, mid-level professional, or seasoned executive, our examples are designed to inspire and guide.',
      'ATS-Friendly Formats: Each example is optimized to pass Applicant Tracking Systems, ensuring your resume gets noticed.',
    ],
    cta: {
      label: 'Explore Resume Examples',
      href: '/resume-examples/',
    },
  },
  {
    id: 'career-guides',
    title: 'Expert Career Guides',
    description:
      'Navigating the job market requires more than just a strong resume—it takes strategy and preparation. That’s where our expert career guides come in.',
    icon: 'BookOpen',
    accent: 'indigo',
    points: [
      'Resume Tips and Formatting: Learn about reverse chronological, functional, and hybrid resume formats.',
      'ATS Optimization: Find out about ATS Resume Scores and learn optimization techniques to build an enhanced resume.',
      'Cover Letter Advice: Write compelling cover letters that complement your resume.',
      'Interview Preparation: Master your next interview with our actionable tips and practice questions.',
      'LinkedIn Optimization: Create a profile that grabs attention and showcases your professional brand.',
    ],
    cta: {
      label: 'Discover Career Guides',
      href: '/guides/',
    },
  },
  {
    id: 'ai-tools',
    title: 'AI-Powered Career Tools',
    description:
      'Gone are the days of static templates. With KudosWall’s AI-powered tools, you can create, analyze, and enhance your career documents in minutes:',
    icon: 'Sparkles',
    accent: 'violet',
    points: [
      'AI Resume Builder: Automatically generate a professional, polished resume tailored to your target industry and job role.',
      'Resume Analyzer: Upload your resume to receive an ATS-friendly score, keyword analysis, and improvement suggestions.',
      'LinkedIn Optimizer: Transform your LinkedIn profile into a recruiter magnet with smart recommendations.',
      'Cover Letter Generator: Craft personalized cover letters that complement your resume and highlight your unique value.',
    ],
    cta: {
      label: 'Get Started with AI Tools',
      href: '/',
    },
  },
  {
    id: 'explore-examples',
    title: 'Explore Resume Examples',
    description:
      'Finding inspiration for your resume has never been easier. Browse our extensive library of resume examples, designed to cover a wide range of industries, roles, and career stages.',
    icon: 'Search',
    accent: 'emerald',
    points: [
      'Fresher Resume Formats: Perfect for those starting their professional journey.',
      'Role-Specific Examples: Explore examples for software engineers, marketing professionals, DevOps specialists, and more.',
      'International Standards: Find resumes tailored for global job markets, including ATS compliance and industry-specific keywords.',
    ],
    cta: {
      label: 'Browse Resume Examples',
      href: '/resume-examples/',
    },
  },
];

export const BLOG_HIGHLIGHTS: BlogHighlightGroup[] = [
  {
    title: 'Trending Guides',
    icon: 'TrendingUp',
    accent: 'teal',
    links: [
      {
        label: 'Guide to Crafting an Effective Resume Headline',
        href: '/guides/resume-headline-guide/',
      },
      { label: 'Guide to Creating Resume in Europass Format', href: '/guides/europass-guide/' },
      { label: 'Germany Lebenslauf Guide (2026)', href: '/guides/germany-lebenslauf-guide/' },
      { label: 'EU Country CV Guide (FR/BE/NL/CH)', href: '/guides/eu-country-cv-guide/' },
      { label: 'Gulf CV vs Europass — Which Format?', href: '/guides/gulf-cv-vs-europass-guide/' },
      { label: 'ATS Resume Score Guide', href: '/guides/ats-resume-score-guide/' },
    ],
  },
  {
    title: 'Featured Resume Examples',
    icon: 'Award',
    accent: 'indigo',
    links: [
      { label: 'Europass CV Examples', href: '/resume-examples/europass/' },
      { label: 'India Resume Format Hub', href: '/resume-examples/india-jobs/' },
      { label: 'UAE / Dubai CV Hub', href: '/resume-examples/uae-jobs/' },
      { label: 'Belgium CV Hub (Brussels EU)', href: '/resume-examples/belgium-jobs/' },
      { label: 'Germany CV Examples', href: '/resume-examples/germany-jobs/' },
    ],
  },
  {
    title: 'Tools Showcase',
    icon: 'Wrench',
    accent: 'violet',
    links: [
      {
        label: 'Analyze Your Resume for Free with Our ATS-Friendly Resume Analyzer',
        href: '/resume-analyzer',
      },
      { label: 'AI Resume Builder: Generate Resume and Enhance in Minutes', href: '/' },
      { label: 'Germany CV Builder (Lebenslauf)', href: '/germany-cv-builder' },
      { label: 'UAE CV Builder', href: '/uae-cv-builder' },
      { label: 'Europass Format Checker', href: '/resume-format-checker/europass' },
      { label: 'Cover Letter Generator', href: '/coverletter' },
    ],
  },
  {
    title: 'Seasonal Picks',
    icon: 'Calendar',
    accent: 'emerald',
    links: [
      {
        label: "Happy Thanksgiving Message from KudosWall's CEO Jag Vootkur",
        href: 'https://blog.kudoswall.com/happy-thanksgiving-34ada69deb36',
        external: true,
      },
      {
        label: 'How to Navigate the Holiday Job Market Slump',
        href: 'https://blog.kudoswall.com/how-to-navigate-the-holiday-job-market-slump-strategies-to-boost-your-job-search-during-the-1b3d5ca4418d',
        external: true,
      },
    ],
  },
];

export const BLOG_RSS_CONFIG = {
  feedUrl: 'https://api.rss2json.com/v1/api.json?rss_url=https://blog.kudoswall.com/feed',
  fallbackUrl: 'https://blog.kudoswall.com',
  maxPosts: 6,
};
